import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import Alert from '../../utils/Alert';
import Loader from '../../utils/Loader';
import Typography from '../../utils/Typography';
import { goToUrl } from '../../utils/utilities';
import { verifyContact } from '../user/actions';

const VERIFIED_REDIRECT_DELAY_MS = 2500;

export function VerifyEmail({ token, emailAddress }) {
  const [error, setError] = useState(null);
  const [verified, setVerified] = useState(false);
  const [returningToInvite, setReturningToInvite] = useState(false);

  useEffect(() => {
    let redirectTimer;
    const payload = emailAddress ? { token, emailAddress } : { token };

    verifyContact(payload)
        .then((response) => {
          const inviteToken = response && response.inviteToken;
          // Both paths show the success confirmation first so the fan knows verification worked,
          // then redirect after a short delay: invited fans go back to the guest-list request flow,
          // everyone else goes to login. The invite redirect forces a full load because App bootstrap
          // only reads ?token= on a document load, not a history.push.
          setVerified(true);
          if (inviteToken) {
            setReturningToInvite(true);
            redirectTimer = setTimeout(
                () => goToUrl(`/?token=${inviteToken}`, { outside: true }),
                VERIFIED_REDIRECT_DELAY_MS,
            );
          } else {
            redirectTimer = setTimeout(() => goToUrl('/login'), VERIFIED_REDIRECT_DELAY_MS);
          }
        })
        .catch((verifyError) => {
          setError(
              verifyError ||
              'We could not verify your email. The link may have expired.',
          );
        });
    // Cancel a pending redirect if the component unmounts before the delay elapses.
    return () => clearTimeout(redirectTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
        <div>
          <Alert type="error"> {error} </Alert>
          <div style={{ margin: '25px 0 0 0', textAlign: 'center' }}>
            <Typography tag="a" secondary onClick={() => goToUrl('/login')}>
              Return to login
            </Typography>
          </div>
        </div>
    );
  }

  if (verified) {
    // Alert hardcodes the error color regardless of `type`, so use a plain heading for the success
    // message rather than a red Alert that would read as a failure.
    return (
        <div style={{ textAlign: 'center' }}>
          <Typography tag="h3">Your email address is verified</Typography>
          <div style={{ margin: '10px 0 0 0' }}>
            <Typography tag="p">
              {returningToInvite
                ? 'Returning you to your ticket request…'
                : 'You can now sign in.'}
            </Typography>
          </div>
          {!returningToInvite && (
              <div style={{ margin: '25px 0 0 0' }}>
                <Typography tag="a" secondary onClick={() => goToUrl('/login')}>
                  Continue to sign in
                </Typography>
              </div>
          )}
        </div>
    );
  }

  return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Loader>Verifying your email</Loader>
      </div>
  );
}

VerifyEmail.propTypes = {
  token: PropTypes.string,
  emailAddress: PropTypes.string,
};

VerifyEmail.defaultProps = {
  token: null,
  emailAddress: null,
};

export default VerifyEmail;
